// ──────────────────────────────────────────────────────
// Frontend-only chat transcript FAST-PAINT CACHE.
//
// Stores up to RECENT_CACHE_TURNS recent chat bubbles per real
// customer in localStorage so the conversation paints instantly
// on remount before the server `GET /api/mobile/history` response
// arrives. The mobile page (and desktop ChatDrawer) treat this as
// a CACHE — not the source of truth. After mount, the page
// fetches authoritative server history and merges/dedupes by id;
// anything beyond this cache will still appear there.
//
// Explicitly frontend-only — no backend endpoint, no authentication
// side effects. Only persists for real customers (anon_* ids are
// treated as "not logged in" so anon-era turns never leak into a
// real account).
//
// Cache size note: RECENT_CACHE_TURNS was 10 before the history API
// existed. We raised it to 30 (matches the server's default first-page
// size) so the fast-paint shape and the server seed line up — the
// merge-by-id step then collapses both sources into a single timeline
// without any visible double-paint. Keeping it modest stays well
// inside the ~5MB localStorage quota even with cards/thumbnails.
// ──────────────────────────────────────────────────────
import type { AssistantBlock, ChatMeta, ProductSuggestion } from './types';

const TRANSCRIPT_KEY_PREFIX = 'qcare_chat_transcript:';

// Recent-conversation cache size. Capped well below the server
// history limit so this stays a fast-paint cache and the server
// history endpoint owns deeper scrollback.
const RECENT_CACHE_TURNS = 30;

// QCARE-IMAGE-UPLOAD-THUMBNAIL-PERSISTENCE-HOTFIX (2026-05-02):
// Hard cap on the per-message persisted thumbnail. Aligned with
// makeImageThumbnail's default (32_000 chars ≈ 24KB raw bytes). 10
// turns × 24KB = ≤240KB total — well within the 5-10MB localStorage
// quota with room for the rest of the transcript shape. Any thumb
// exceeding this is dropped at write time so a future helper
// regression can't blow quota.
const MAX_THUMB_DATA_URL_CHARS = 32_000;

export interface PersistedTurn {
    role: 'user' | 'ai';
    text: string;
    // Named `chips` for the persisted shape. Surface code maps to/from its
    // own field (desktop uses `suggestions`, mobile uses `chips`) so the
    // on-disk contract stays stable even if a surface renames its local
    // vocab.
    chips?: string[];
    // Only persisted when it's a safe http/https URL. Base64 `data:` URLs
    // and `blob:` URLs are dropped to keep localStorage under quota.
    imageUrl?: string;
    // QCARE-IMAGE-UPLOAD-THUMBNAIL-PERSISTENCE-HOTFIX (2026-05-02):
    // Small JPEG data URL (≤32_000 chars) produced by
    // lib/image-thumbnail.ts so the upload preview survives refresh.
    imageThumbUrl?: string;
    // Stable per-bubble id minted on first render. Preserved across
    // refresh so the restored AI message still matches its backend
    // feedback row, and so future 👍/👎 clicks on that bubble can
    // POST a persistent messageId instead of being silently dropped.
    id?: string;
    // ── Card-payload passthrough (additive; AI messages only in practice) ──
    // `blocks` is the V3 structured render tree (product_title / product_card
    // / bridge_text / per-product narrative `text`). `productSuggestions` is
    // the legacy flat card array read by the fallback render path. `meta`
    // carries layout hints (hero_plus_budget etc.) that drive CardRenderer
    // dispatch. All three are JSON-serializable through existing types; no
    // binary data, no base64 — safe to round-trip through localStorage.
    // Absence on restored legacy transcripts is tolerated by the render
    // cascade (falls back to text-only bubbles) so the field set is a pure
    // additive extension with zero required-field churn.
    blocks?: AssistantBlock[];
    productSuggestions?: ProductSuggestion[];
    meta?: ChatMeta;
}

function isEligibleId(customerId: string | null | undefined): customerId is string {
    return typeof customerId === 'string'
        && customerId.length > 0
        && !customerId.startsWith('anon_');
}

/**
 * Load up to RECENT_CACHE_TURNS most recent turns for this customer. Returns an
 * empty array for anonymous / unknown ids, SSR contexts, malformed JSON,
 * or quota read failures — the caller never sees an exception.
 */
export function loadTranscript(customerId: string | null | undefined): PersistedTurn[] {
    if (!isEligibleId(customerId)) return [];
    if (typeof window === 'undefined') return [];
    try {
        const raw = window.localStorage.getItem(TRANSCRIPT_KEY_PREFIX + customerId);
        if (!raw) return [];
        const arr = JSON.parse(raw);
        if (!Array.isArray(arr)) return [];
        return arr
            .slice(-RECENT_CACHE_TURNS)
            .filter((t: unknown): t is PersistedTurn =>
                !!t
                && typeof (t as { role?: unknown }).role === 'string'
                && ((t as { role?: unknown }).role === 'user' || (t as { role?: unknown }).role === 'ai')
                && typeof (t as { text?: unknown }).text === 'string',
            )
            .map(t => {
                // Spread preserves any optional fields already written to
                // disk (blocks / productSuggestions / meta / imageUrl /
                // chips). We only explicitly coerce `id` because the
                // original contract allowed arbitrary stored types for
                // it; everything else is passed through as-is. Malformed
                // entries would have been dropped by the filter above.
                const row = t as PersistedTurn;
                return {
                    ...row,
                    id: typeof (row as { id?: unknown }).id === 'string' ? row.id : undefined,
                };
            });
    } catch {
        return [];
    }
}

/**
 * Persist up to RECENT_CACHE_TURNS most recent turns. No-op for anonymous / unknown
 * ids, SSR contexts, or quota exceeded. Heavy payloads are stripped before
 * write so a long conversation still fits.
 */
export function saveTranscript(customerId: string | null | undefined, turns: PersistedTurn[]): void {
    if (!isEligibleId(customerId)) return;
    if (typeof window === 'undefined') return;
    const clean = turns.slice(-RECENT_CACHE_TURNS).map(t => ({
        role: t.role,
        text: t.text,
        ...(Array.isArray(t.chips) && t.chips.length > 0 ? { chips: t.chips } : {}),
        ...(t.imageUrl && /^https?:\/\//i.test(t.imageUrl) ? { imageUrl: t.imageUrl } : {}),
        // QCARE-IMAGE-UPLOAD-THUMBNAIL-PERSISTENCE-HOTFIX (2026-05-02):
        // Persist the small JPEG thumbnail. Strict guardrails:
        //  • must be a data URL (we never persist arbitrary external URLs here)
        //  • must be a JPEG (matches makeImageThumbnail's output format)
        //  • must be ≤ MAX_THUMB_DATA_URL_CHARS to avoid quota exhaustion
        //    if a future helper regression produces a fat thumb.
        // Anything failing these checks is silently dropped — the surface
        // falls back to no-thumbnail behavior on refresh.
        ...(typeof t.imageThumbUrl === 'string'
            && t.imageThumbUrl.startsWith('data:image/jpeg;base64,')
            && t.imageThumbUrl.length <= MAX_THUMB_DATA_URL_CHARS
                ? { imageThumbUrl: t.imageThumbUrl }
                : {}),
        ...(typeof t.id === 'string' && t.id.length > 0 ? { id: t.id } : {}),
        // Card-payload passthrough — only included when present on the
        // in-memory turn, so user messages (which never carry blocks /
        // product suggestions / meta) stay compact. Same emit pattern
        // as chips/imageUrl/id above.
        ...(Array.isArray(t.blocks) && t.blocks.length > 0 ? { blocks: t.blocks } : {}),
        ...(Array.isArray(t.productSuggestions) && t.productSuggestions.length > 0 ? { productSuggestions: t.productSuggestions } : {}),
        ...(t.meta ? { meta: t.meta } : {}),
    }));
    try {
        window.localStorage.setItem(TRANSCRIPT_KEY_PREFIX + customerId, JSON.stringify(clean));
    } catch {
        // quota exceeded or localStorage disabled — silent
    }
}

export { TRANSCRIPT_KEY_PREFIX, RECENT_CACHE_TURNS };
